import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'
import { differenceInDays, format, subDays } from 'date-fns'

import { depositsCalculatorOfferClicked, leadInitialized } from 'common/actions'
import { PublicClassNames } from 'embed/constants'
import { isDev } from 'embed/constants/config'
import { formatCurrency, formatImageUrl, getContrastingColor, getOfferNumericFieldValue, padRate } from 'embed/utils'
import { MultiProductState } from 'embed/reducers'

import Modal from 'embed/components/Modal'
import TrackingPixel from 'embed/components/TrackingPixel'
import { DepositsOffer } from 'embed/constants/models'
import { Filter, Product } from 'even-ts-static'
import { CASH_RETURN_PREFIX, DEFAULT_AMOUNT } from '..'
import { useTheme } from 'ui/multi-product/hooks'

const styles = require('embed/styles/results')
const savingsCalcStyles = require('./styles')

const APY_POSTFIX = '%'

export interface DispatchProps {
  dispatchLeadInitialized: (leadUuid: string) => void
  dispatchOfferCta: (offer: DepositsOffer) => void
}

export interface OwnProps {
  bestOfferId?: string
  offer: DepositsOffer
  viewportHeight?: number
  viewportWidth?: number
}

export interface StoreProps {
  depositAmountIntent?: number
  leadUuid?: string
  rateTableUuid?: string
}

export type ListItemProps = OwnProps & DispatchProps & StoreProps

const ListItem: React.FC<ListItemProps> = ({
  dispatchOfferCta,
  depositAmountIntent,
  leadUuid,
  offer,
  rateTableUuid,
}) => {
  const el: React.MutableRefObject<HTMLLIElement | null> = React.useRef(null)
  const [disclaimerModalOffset, setDisclaimerModalOffset] = React.useState(0)
  const [disclaimerModalVisible, setDisclaimerModalVisible] = React.useState(false)

  const depositsOfferCta = React.useCallback(
    (event: React.MouseEvent<Element>) => {
      // Prevent multiple windows from opening on offer click due to multiple click targets
      event.stopPropagation()
      dispatchOfferCta(offer)
    },
    [dispatchOfferCta, offer],
  )

  const toggleDisclaimerModal = React.useCallback(
    (event: React.MouseEvent<Element>) => {
      event.stopPropagation()
      setDisclaimerModalOffset(el.current?.offsetTop ?? 0)
      setDisclaimerModalVisible(!disclaimerModalVisible)
    },
    [setDisclaimerModalOffset, setDisclaimerModalVisible, disclaimerModalVisible, el],
  )

  // Partner details
  const disclaimerDetails = offer.details?.description
  const disclaimerMoreInfo = offer.partner?.disclaimer

  // APY Data
  const annualPercentYield = (offer.details?.annualPercentYield ?? 0) / 100

  const annualCashReturn = `${CASH_RETURN_PREFIX}${formatCurrency(
    (depositAmountIntent === undefined || isNaN(depositAmountIntent) ? DEFAULT_AMOUNT : depositAmountIntent) *
      annualPercentYield,
  )}`
  const annualPercentYieldString = getOfferNumericFieldValue(offer.details.annualPercentYield, {
    postfix: APY_POSTFIX,
    transformValue: padRate,
  })

  const offerEffectiveAsDate = new Date(offer.details.effectiveAsOf)
  const effectiveAsOfDate =
    differenceInDays(new Date(), offerEffectiveAsDate) > 7
      ? format(subDays(new Date(), 2), 'MM/dd/yyyy')
      : format(offerEffectiveAsDate, 'MM/dd/yyyy')

  const {
    fontFamilyPrimary,
    borderRadiusLarge,
    borderRadiusSmall,
    colorBorder,
    ctaStyles,
    colorTextPrimary,
    colorTextSecondary,
  } = useTheme()

  const disclaimerModal = (
    <Modal offset={disclaimerModalOffset} onDismiss={toggleDisclaimerModal}>
      {disclaimerDetails && (
        <div className={styles.modalDisclaimer}>
          <h4
            className={css`
              font-family: ${fontFamilyPrimary};
            `}
          >
            More Information
          </h4>
          <p dangerouslySetInnerHTML={{ __html: disclaimerDetails }} />
        </div>
      )}
      {disclaimerMoreInfo && (
        <div className={styles.modalDisclaimer}>
          <h4
            className={css`
              font-family: ${fontFamilyPrimary};
            `}
          >
            Disclaimer
          </h4>
          <p dangerouslySetInnerHTML={{ __html: disclaimerMoreInfo }} />
        </div>
      )}
    </Modal>
  )

  const trackingPixel = leadUuid && rateTableUuid && (
    <TrackingPixel leadUuid={leadUuid} rateTableUuid={rateTableUuid} offerUuid={offer.uuid} />
  )

  return (
    <li
      className={classnames(
        PublicClassNames.Offer,
        savingsCalcStyles.offer,
        css`
          border-color: ${colorBorder};
          border-radius: ${borderRadiusLarge};
        `,
      )}
      ref={el}
      data-test-val={isDev ? offer.uuid : ''}
    >
      {trackingPixel}
      <div className={classnames(savingsCalcStyles.offerWrapper)}>
        <div className={savingsCalcStyles.columnPartner}>
          <img
            alt={offer.details?.name}
            className={styles.partnerImage}
            src={formatImageUrl(offer.partner?.imageUrl)}
          />
        </div>
        <div className={savingsCalcStyles.columnReturn}>
          <div
            className={classnames(
              savingsCalcStyles.statTitle,
              css`
                color: ${colorTextSecondary};
              `,
            )}
          >
            Estimated Annual Cash Return
          </div>
          <div
            className={classnames(
              savingsCalcStyles.statLarge,
              css`
                font-family: ${fontFamilyPrimary};
              `,
            )}
          >
            <span>{annualCashReturn}</span>
          </div>
        </div>
        <div className={savingsCalcStyles.columnApy}>
          <div
            className={classnames(
              savingsCalcStyles.statTitle,
              css`
                color: ${colorTextSecondary};
              `,
            )}
          >
            APY
          </div>
          <div
            className={classnames(
              savingsCalcStyles.statSmall,
              css`
                color: ${colorTextSecondary};
                font-family: ${fontFamilyPrimary};
              `,
            )}
          >
            <span data-test-id="offer_apy">{annualPercentYieldString}</span>
          </div>
        </div>
      </div>
      <div className={savingsCalcStyles.columnCta}>
        <button
          className={classnames(
            savingsCalcStyles.button,
            css(`
                color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
                border-radius: ${borderRadiusSmall};
                text-transform: capitalize;
                &:hover {opacity: 0.84;}
              `),
          )}
          style={ctaStyles}
          onClick={depositsOfferCta}
        >
          Get This Offer
        </button>
      </div>

      <div className={savingsCalcStyles.disclaimerRow}>
        {!!disclaimerModalVisible && disclaimerModal}
        <div
          className={classnames(
            savingsCalcStyles.label,
            savingsCalcStyles.disclaimerLink,
            css(`color: ${colorTextSecondary}`),
          )}
          onClick={toggleDisclaimerModal}
        >
          Disclaimer &amp; more info
        </div>
        <div className={classnames(savingsCalcStyles.label, css(`color: ${colorTextSecondary}`))}>
          As of {effectiveAsOfDate}
        </div>
      </div>
    </li>
  )
}

const mapStateToProps = (state: MultiProductState, ownProps: OwnProps) => ({
  ...ownProps,
  depositAmountIntent: state.filters[Product.Deposits]?.[Filter.DepositAmountIntentValue],
  leadUuid: state.rates[Product.Deposits].leadUuid,
  rateTableUuid: state.rates[Product.Deposits].uuid,
})

const mapDispatchToProps = (dispatch) => ({
  dispatchLeadInitialized: (leadUuid: string) => dispatch(leadInitialized({ uuid: leadUuid })),
  dispatchOfferCta: (offer: DepositsOffer) => dispatch(depositsCalculatorOfferClicked(offer)),
})

export const DepositsOfferListItem = connect(mapStateToProps, mapDispatchToProps)(ListItem)
